import { useQueryClient } from '@tanstack/react-query'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import LinkIcon from 'ext-ui/svg/icon/LinkIcon'
import React, { KeyboardEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import { amexIssuerCodes } from 'src/app/RegisterAccount/RegisterAccount'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import ConnectPhysicalCardModal from 'src/component-modal/ConnectPhysicalCardModal/ConnectPhysicalCardModal'
import DisconnectPhysicalCardModal from 'src/component-modal/DisconnectPhysicalCardModal/DisconnectPhysicalCardModal'
import { Issuers, SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardDisplayStatus,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { creditCardsQueryKeys } from 'src/queries/queries.constants'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

import style from './PhysicalCardTransactionsPanel.style'

type PhysicalCardTransactionsPanelProps = {
  className?: string
  creditCard: CreditCardListModel
  canConnect?: boolean
}

export default function PhysicalCardTransactionsPanel(
  props: PhysicalCardTransactionsPanelProps,
) {
  const { className = '', creditCard, canConnect = true } = props
  const queryClient = useQueryClient()
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false)
  const [showDisconnectModal, setShowDisconnectModal] = useState<boolean>(false)
  const [isVerifying, setIsVerifying] = useState<boolean>(false)

  const [cardStatus, setCardStatus] = useState<PhysicalCardStatus | null>(
    creditCard.physicalCard.status,
  )
  const [showDisconnectAlertPanel, setShowDisconnectAlertPanel] =
    useState<boolean>(false)

  const { whiteLabelIncludes } = useWhitelabel()
  const translate = useTranslate()
  const dispatch = useDispatch()

  const isAmex = amexIssuerCodes.includes(creditCard.issuer as Issuers)

  const shouldDarkenPanel = whiteLabelIncludes([
    SupportedBrands.Boc,
    SupportedBrands.Pnc,
    SupportedBrands.Bmo,
    SupportedBrands.Corpay,
    SupportedBrands.PacWest,
  ])

  if (
    whiteLabelIncludes(SupportedBrands.Jpm) ||
    isAmex ||
    !creditCard.physicalCard.status ||
    creditCard.physicalCard.status === PhysicalCardStatus.NotApplicable ||
    creditCard.physicalCard.status === PhysicalCardStatus.Closed ||
    creditCard.displayStatus === CreditCardDisplayStatus.Closed
  ) {
    return null
  }

  // For the time being as darkened variants are coming and not in Figma, let's just
  // hide the darkened state and not show the panel at all to keep consistent
  if (shouldDarkenPanel) {
    return null
  }

  const handleConnectClick = () => {
    setShowConnectModal(true)
  }

  const handleDisconnectClick = () => {
    setShowDisconnectModal(true)
  }

  const isActive = cardStatus === PhysicalCardStatus.Active

  const shouldShowConnectCTA =
    canConnect &&
    (cardStatus === PhysicalCardStatus.Inactive ||
      cardStatus === PhysicalCardStatus.NeedsVerification ||
      cardStatus === PhysicalCardStatus.Pending ||
      cardStatus === PhysicalCardStatus.EnrollmentAvailable ||
      cardStatus === null)

  return (
    <div
      className={cn('PhysicalCardTransactionsPanel', className, {
        darkened: shouldDarkenPanel,
        'has-top-alert-panel': showDisconnectAlertPanel,
      })}
      css={style}
    >
      {showDisconnectAlertPanel && (
        <AlertPanel variant="warning">
          <div className="flex-row gap-4 align-top">
            <AlertTriangleYellowIcon />
            <div className="flex-column gap-2">
              <p className="font-bold">
                {translate('Disconnected Integration')}
              </p>
              <p className="font-regular">
                {translate(
                  'Your physical card transaction feed is disconnected.',
                )}
              </p>
            </div>
          </div>
        </AlertPanel>
      )}
      <ContentCard variant={shouldDarkenPanel ? 'darkened' : 'standard'}>
        <div className="flex-column gap-5">
          <div className="flex-row justify-start gap-2">
            {isActive && <CheckmarkCircleIcon />}
            <p className="font-h3">
              {translate(
                isActive
                  ? 'Physical Card Transactions Enabled'
                  : 'Enable Physical Card Transactions',
              )}
            </p>
          </div>

          <p className="font-regular">
            {translate(
              'All transactions from your credit card will automatically import into Extend for a comprehensive view of card spend.',
            )}
          </p>
        </div>
      </ContentCard>
      <div className="PhysicalCardTransactionsPanel-bottom">
        {shouldShowConnectCTA && (
          <div
            className="linktext-content"
            role="button"
            onClick={handleConnectClick}
            tabIndex={0}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                handleConnectClick()
              }
            }}
          >
            <div className="flex-row align-center gap-2">
              <p className="font-linktext connect-text">
                {translate('Connect')}
              </p>
              <LinkIcon />
            </div>
          </div>
        )}
        {isActive && (
          <div
            className="linktext-content"
            role="button"
            onClick={handleDisconnectClick}
            tabIndex={0}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                handleDisconnectClick()
              }
            }}
          >
            <p className="disconnect-text font-linktext">{'Disconnect'}</p>
          </div>
        )}
      </div>
      <ConnectPhysicalCardModal
        isOpen={showConnectModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConnectModal(false)
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        reviewEnrollCardProps={{
          onBack: () => {
            setShowConnectModal(false)
          },
          creditCardId: creditCard.id,
          onVerifyStart: () => {
            setIsVerifying(true)
          },
          onVerifyComplete: () => {
            setIsVerifying(false)
            setShowConnectModal(false)
            setCardStatus(PhysicalCardStatus.Active)
            // Show a toast here
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <p>{translate('Success! Your card is now active.')}</p>
                ),
              }),
            )
            // Keep AccountSettings's creditCard.physicalCard.status in sync so
            // visibility recomputes on the latest server state.
            queryClient.invalidateQueries({
              queryKey: creditCardsQueryKeys.detail(creditCard.id),
            })
          },
          onVerifyError: () => {
            setIsVerifying(false)
          },
          submissionData: {
            binData: {
              id: creditCard.issuerId,
              // Since this is a physical card transaction, we do not need to
              // show acknowledgements for anything but they PHYSICAL_ONLY flow
              eligibility: 'PHYSICAL_ONLY',
              issuerLogoImg: creditCard.cardImage.urls.small,
              issuerRequiresVerification: false,
            },
            addressData: {
              cardName: creditCard.displayName,
              ...creditCard.address,
            },
            cardData: {
              // We only have access to the last 4 and not the CVC in this view
              // So I think this might be our best bet in getting this to work
              // The component itself already masks the card number minus the last
              // 4 digits so this should be alright
              number: `**** **** **** ${creditCard.last4}`,
              // We are unable to get CVC info from the card detail view since
              // this data lives in VGS and it may be a nasty security issue
              // to display it here if a user's account is compromised
              cvc: '***',
              expiration: creditCard.expiresFormattedString,
            },
          },
        }}
      />
      <DisconnectPhysicalCardModal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false)
        }}
        onDisconnectComplete={() => {
          setShowDisconnectAlertPanel(true)
          setShowDisconnectModal(false)
          setCardStatus(PhysicalCardStatus.Inactive)
          queryClient.invalidateQueries({
            queryKey: creditCardsQueryKeys.detail(creditCard.id),
          })
        }}
        creditCardId={creditCard.id}
      />
    </div>
  )
}
